import React from 'react'
import { Disclosure, Transition } from '@headlessui/react'
import { ChevronUpIcon } from '@heroicons/react/solid'

type Button = {
  buttonStyle: string
  openButtonStyle?: string
  buttonText: string | React.ReactNode
  openIcon?: React.ReactNode
  closeIcon?: React.ReactNode
}

type Transition = {
  enter?: string
  enterFrom?: string
  enterTo?: string
  leave?: string
  leaveFrom?: string
  leaveTo?: string
  show?: boolean
}

type Panel = {
  panelStyle: string
  openPanelStyle?: string
  transitionsProps: Transition
}

type AccordionItemStyles = {
  buttonProps?: Button
  panelProps?: Panel
  isOpen?: boolean
  multipleOpen?: boolean
  children: React.ReactNode
  indexKey?: number
}

const defaultButtonProps: Button = {
  buttonStyle:
    'flex justify-between w-full px-6 py-3 bg-gray200 text-base font-bold text-left text-zinc-900 border-y rounded',
  openButtonStyle:
    'flex justify-between w-full px-6 py-3 bg-gray200 text-base font-bold text-left text-black border-y rounded',
  buttonText: 'Payment Method',
  openIcon: <ChevronUpIcon className='h-5 w-5 rotate-180 transform' />,
  closeIcon: <ChevronUpIcon className='h-5 w-5' />
}

const defaultTransitionProps: Transition = {
  enter: 'transition duration-100 ease-out',
  enterFrom: 'transform scale-95 opacity-0',
  enterTo: 'transform scale-100 opacity-100',
  leave: 'transition duration-75 ease-out',
  leaveFrom: 'transform scale-100 opacity-100',
  leaveTo: 'transform scale-95 opacity-0'
}

const defaultPanelProps: Panel = {
  panelStyle: 'px-4 pt-4 pb-2 text-sm text-gray-500',
  openPanelStyle: 'px-4 pt-4 pb-2 text-sm text-gray-500 opened',
  transitionsProps: defaultTransitionProps
}

const AccordionItem = ({
  buttonProps = defaultButtonProps,
  panelProps = defaultPanelProps,
  isOpen,
  multipleOpen = true,
  children
}: AccordionItemStyles) => {
  const { openIcon, closeIcon } = buttonProps
  return (
    <Disclosure defaultOpen={isOpen}>
      {({ open }) => (
        <>
          <Disclosure.Button
            className={
              (multipleOpen ? open : isOpen) && buttonProps?.openButtonStyle
                ? buttonProps.openButtonStyle
                : buttonProps.buttonStyle
            }
          >
            {buttonProps.buttonText}
            {(multipleOpen ? open : isOpen) ? closeIcon : openIcon}
          </Disclosure.Button>

          <Transition
            show={panelProps.transitionsProps.show}
            enter={panelProps.transitionsProps.enter}
            enterFrom={panelProps.transitionsProps.enterFrom}
            enterTo={panelProps.transitionsProps.enterTo}
            leave={panelProps.transitionsProps.leave}
            leaveFrom={panelProps.transitionsProps.leaveFrom}
            leaveTo={panelProps.transitionsProps.leaveTo}
          >
            {(multipleOpen || isOpen) && (
              <Disclosure.Panel
                className={
                  (multipleOpen ? open : isOpen) && panelProps?.panelStyle
                    ? panelProps.openPanelStyle
                    : panelProps.panelStyle
                }
              >
                {children}
              </Disclosure.Panel>
            )}
          </Transition>
        </>
      )}
    </Disclosure>
  )
}

export default AccordionItem
